/**
 * Campaign attribution — first-touch and last-touch UTM capture.
 *
 * Captures UTM params on *every* page the visitor lands on and persists them,
 * so a lead or a Book Now click can be credited to the campaign that brought
 * the visitor in — even when they arrived on the homepage days earlier and
 * only converted after browsing.
 *
 * Deliberately framework-free (no Vue, no vue-router imports) so this file
 * can be lifted into ctoc-web as a client-only plugin at cutover without a
 * rewrite.
 *
 * Storage layout:
 *   localStorage   ctoc_cid          anonymous visitor id, ~1 year
 *   sessionStorage ctoc_sid          session id, one per tab session
 *   localStorage   ctoc_attr_first   first campaign that ever brought them
 *   localStorage   ctoc_attr_last    most recent campaign
 *
 * Every storage access is guarded: Safari private browsing throws on read
 * and write, and the site must keep working with no attribution at all.
 */

const CID_KEY = "ctoc_cid";
const SID_KEY = "ctoc_sid";
const FIRST_KEY = "ctoc_attr_first";
const LAST_KEY = "ctoc_attr_last";

const TTL_DAYS = 90;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

const UTM_KEYS = ["source", "medium", "campaign", "term", "content"];

/* -------------------------------------------------------------------------
 * Storage helpers — never throw, never break the page
 * ---------------------------------------------------------------------- */

function safeGet(store, key) {
  try {
    const raw = window[store].getItem(key);
    return raw == null ? null : raw;
  } catch (err) {
    return null;
  }
}

function safeSet(store, key, value) {
  try {
    window[store].setItem(key, value);
    return true;
  } catch (err) {
    return false;
  }
}

function readJson(store, key) {
  const raw = safeGet(store, key);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (err) {
    return null;
  }
}

function writeJson(store, key, value) {
  try {
    return safeSet(store, key, JSON.stringify(value));
  } catch (err) {
    return false;
  }
}

/* -------------------------------------------------------------------------
 * Ids
 * ---------------------------------------------------------------------- */

function newId() {
  try {
    // Unavailable on non-secure origins, so it can't be assumed.
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
  } catch (err) {
    // fall through
  }
  return (
    Date.now().toString(36) +
    "-" +
    Math.random().toString(36).slice(2, 10) +
    Math.random().toString(36).slice(2, 10)
  );
}

function getClientId() {
  let id = safeGet("localStorage", CID_KEY);
  if (!id) {
    id = newId();
    safeSet("localStorage", CID_KEY, id);
  }
  return id;
}

function getSessionId() {
  let id = safeGet("sessionStorage", SID_KEY);
  if (!id) {
    id = newId();
    safeSet("sessionStorage", SID_KEY, id);
  }
  return id;
}

/* -------------------------------------------------------------------------
 * Touch records
 * ---------------------------------------------------------------------- */

function isExpired(touch) {
  if (!touch || !touch.at) return true;
  const at = Date.parse(touch.at);
  if (Number.isNaN(at)) return true;
  return Date.now() - at > TTL_MS;
}

function readTouch(key) {
  const touch = readJson("localStorage", key);
  return isExpired(touch) ? null : touch;
}

/**
 * Reads the UTM params off the current URL. Returns null when the URL
 * carries no campaign tags at all.
 */
function readUtmFromUrl() {
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch (err) {
    return null;
  }

  const utm = {};
  let tagged = false;
  UTM_KEYS.forEach((key) => {
    const value = params.get(`utm_${key}`);
    if (value) {
      // Cap length so a junk URL can't blow past the API's string limits.
      utm[key] = String(value).slice(0, 200);
      tagged = true;
    }
  });

  return tagged ? utm : null;
}

/**
 * True when the referrer is a different site. A referrer from our own
 * domain is an internal click, not a new acquisition.
 */
function isExternalReferrer() {
  try {
    const ref = document.referrer;
    if (!ref) return false;
    return new URL(ref).hostname !== window.location.hostname;
  } catch (err) {
    return false;
  }
}

function buildTouch(utm) {
  const touch = { at: new Date().toISOString() };
  UTM_KEYS.forEach((key) => {
    touch[key] = utm && utm[key] ? utm[key] : null;
  });

  try {
    touch.referrer = document.referrer
      ? document.referrer.slice(0, 1000)
      : null;
  } catch (err) {
    touch.referrer = null;
  }

  try {
    touch.landingPath = window.location.pathname.slice(0, 500);
  } catch (err) {
    touch.landingPath = null;
  }

  return touch;
}

/* -------------------------------------------------------------------------
 * Capture
 * ---------------------------------------------------------------------- */

/**
 * Records the current URL as a touch, if it qualifies. Safe to call on every
 * navigation — it is idempotent for untagged internal moves.
 *
 * Last-touch is only overwritten by a *tagged* arrival or an external
 * referral. Overwriting on every internal navigation would reset every
 * visitor to "direct" the moment they clicked a second page, which is the
 * usual way hand-rolled attribution silently reports nothing.
 *
 * `allowReferrerTouch` must be false for in-app route changes. document.referrer
 * keeps naming the original external site for the whole SPA session, so on a
 * route change it is stale — honouring it there would let an untagged internal
 * click overwrite the campaign the visitor actually arrived on.
 */
export function captureFromUrl({ allowReferrerTouch = false } = {}) {
  if (typeof window === "undefined") return;

  const utm = readUtmFromUrl();
  const qualifies =
    Boolean(utm) || (allowReferrerTouch && isExternalReferrer());
  if (!qualifies) return;

  const touch = buildTouch(utm);

  if (!readTouch(FIRST_KEY)) {
    writeJson("localStorage", FIRST_KEY, touch);
  }
  writeJson("localStorage", LAST_KEY, touch);
}

/**
 * Called once on app boot. Seeds the ids so every conversion — tagged or
 * not — can be tied back to a visitor, then captures the landing URL.
 */
export function initAttribution() {
  if (typeof window === "undefined") return;
  getClientId();
  getSessionId();
  // Full page load: document.referrer is meaningful here and nowhere else.
  captureFromUrl({ allowReferrerTouch: true });
}

/* -------------------------------------------------------------------------
 * Read
 * ---------------------------------------------------------------------- */

/**
 * The full attribution record, as sent to the API.
 */
export function getAttribution() {
  if (typeof window === "undefined") {
    return { clientId: null, sessionId: null, first: null, last: null };
  }
  return {
    clientId: getClientId(),
    sessionId: getSessionId(),
    first: readTouch(FIRST_KEY),
    last: readTouch(LAST_KEY),
  };
}

/**
 * Last-touch campaign narrowed to the three keys the existing `meta.utm`
 * field accepts. Keeps the stored `utm` column and every query written
 * against it working unchanged, while the richer record rides alongside
 * in `meta.attribution`.
 */
export function getUtmForMeta() {
  const last = typeof window === "undefined" ? null : readTouch(LAST_KEY);
  const utm = {};
  if (!last) return utm;
  ["source", "medium", "campaign"].forEach((key) => {
    if (last[key]) utm[key] = last[key];
  });
  return utm;
}
